import { useContext, useMemo, useCallback } from "react";

import { styled } from "@compiled/react";
import {
    GetFastshiftSupportRequestsResponseData,
    IBxpWebStargateClient,
    useBxpWebStargateClient
} from "@wac-web/libraries/bxp-web-stargate-client/src";
import { FormattedMessage, useIntl } from "react-intl";

import ButtonGroup from "@atlaskit/button/button-group";
import Button from "@atlaskit/button-next/new";
import Form, {
    ErrorMessage,
    Field,
    HelperMessage,
    RequiredAsterisk
} from "@atlaskit/form";
import { ModalBody, ModalFooter } from "@atlaskit/modal-dialog";
import { Text, Stack } from "@atlaskit/primitives-next";
import Select, { type ValueType } from "@atlaskit/select";
import TextArea from "@atlaskit/textarea";
import { token } from "@atlaskit/tokens";

import { SAC_URL } from "@/common/routes/constants";
import Flag, { CustomFlagProps } from "@/contexts/FlagProvider";

import { useRelationshipsAnalytics } from "../../../hooks";
import { isSupportRequestOpen } from "../../../utils";
import { messages } from "../messages";

import { createMoveTicketComment } from "./comment-mutation-query";

interface Option {
    label: string;
    site: string;
    product: string;
    value: string;
}

const StyledTextAreaWraper = styled.div({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    textarea: {
        border: `1px solid ${token("color.border")}`
    }
});

export const FastshiftAssistanceModalForm = ({
    handleCloseModal,
    supportRequests,
    isSupportRequestsLoading
}: {
    handleCloseModal: () => void;
    supportRequests: GetFastshiftSupportRequestsResponseData;
    isSupportRequestsLoading: boolean;
}) => {
    const intl = useIntl();
    const stargateClient = useBxpWebStargateClient();
    const { showFlag } = useContext(Flag.Context);

    const {
        sendRequestAssistanceModalCommentSubmitButtonClickUIEvent,
        sendPostCommentOutcomeFlagDisplayedTrackEvent
    } = useRelationshipsAnalytics();

    const customerSupportRequestOptions: Option[] = useMemo(
        () =>
            supportRequests
                .filter((request) => isSupportRequestOpen(request.status))
                .map((request) => {
                    const productName = "";
                    let siteDomainName = "";

                    try {
                        siteDomainName = request.destination.baseUrl
                            ? new URL(request.destination.baseUrl).hostname
                            : "";
                    } catch {
                        siteDomainName = "";
                    }

                    return {
                        label: `${request.supportTicketId} - ${request.title}`,
                        site: siteDomainName,
                        product: productName,
                        value: request.supportTicketId
                    };
                }),
        [supportRequests]
    );

    const successFlag = useCallback(
        ({ ticketId }: { ticketId: string }): CustomFlagProps => {
            return {
                id: "success-flag",
                iconType: "success",
                styleOverride: true,
                autoDismiss: true,
                title: intl.formatMessage(
                    messages.requestAssistanceModalSuccessFlagTitle
                ),
                description: intl.formatMessage(
                    messages.requestAssistanceModalSuccessFlagDescription
                ),
                actions: [
                    {
                        content: intl.formatMessage(
                            messages.requestAssistanceModalSuccessFlagAction
                        ),
                        href: `${SAC_URL}/requests/${ticketId}`,
                        target: "_blank"
                    }
                ]
            };
        },
        [intl]
    );

    const errorFlag = useMemo((): CustomFlagProps => {
        return {
            id: "error-flag",
            iconType: "error",
            styleOverride: true,
            autoDismiss: true,
            title: intl.formatMessage(
                messages.requestAssistanceModalErrorFlagTitle
            ),
            description: intl.formatMessage(
                messages.requestAssistanceModalErrorFlagDescription
            )
        };
    }, [intl]);

    const createMoveTicketCommentRequest = ({
        stargateClient,
        issueId,
        comment
    }: {
        stargateClient: IBxpWebStargateClient;
        issueId: string;
        comment: string;
    }) => {
        return new Promise((resolve, reject) =>
            createMoveTicketComment({
                stargateClient,
                issueId,
                comment
            })
                .then((response) => {
                    if (
                        !response ||
                        !response?.data?.customerSupport?.addComment
                    ) {
                        // if response payload is not valid, the comment was not created, regardless of the HTTP status code
                        reject(
                            new Error(
                                "Server failed to execute the request - comment was not created"
                            )
                        );
                    }
                    resolve(response);
                })
                .catch((error) => {
                    reject(error);
                })
        );
    };

    // Search for items by ticket id, summary, site domain name, or product name
    const searchDropdownOptions = (option: Option, inputValue: string) => {
        const lowerCaseInputValue = inputValue.toLowerCase();
        return (
            option.label.toLowerCase().includes(lowerCaseInputValue) ||
            option.site.toLowerCase().startsWith(lowerCaseInputValue) ||
            option.product.toLowerCase().startsWith(lowerCaseInputValue)
        );
    };

    return (
        <Form<{ ticketId: Option; comment: string }>
            onSubmit={(data) => {
                // validate form fields
                if (!data.ticketId || !data.comment) {
                    const errors = {
                        ticketId: !data.ticketId
                            ? intl.formatMessage(
                                  messages.requestAssistanceModalFieldIsRequiredMessage
                              )
                            : undefined,
                        comment: !data.comment
                            ? intl.formatMessage(
                                  messages.requestAssistanceModalFieldIsRequiredMessage
                              )
                            : undefined
                    };
                    return errors;
                }

                // Fire UI event for button click
                sendRequestAssistanceModalCommentSubmitButtonClickUIEvent(
                    data.ticketId.value
                );

                return createMoveTicketCommentRequest({
                    stargateClient,
                    issueId: data.ticketId.value,
                    comment: data.comment
                })
                    .then(() => {
                        // Show success flag and track event
                        showFlag(
                            successFlag({
                                ticketId: data.ticketId.value
                            })
                        );
                        sendPostCommentOutcomeFlagDisplayedTrackEvent(
                            true,
                            data.ticketId.value
                        );

                        handleCloseModal();
                    })
                    .catch((error) => {
                        console.error(error);

                        // Show error flag and track event
                        showFlag(errorFlag);
                        sendPostCommentOutcomeFlagDisplayedTrackEvent(
                            false,
                            data.ticketId.value
                        );
                    });
            }}
        >
            {({ formProps, submitting }) => (
                <form {...formProps}>
                    <ModalBody>
                        <Stack space="space.200">
                            <HelperMessage>
                                <Text color="color.text.subtle">
                                    <FormattedMessage
                                        {...messages.requestAssistanceModalSubtitleNote}
                                    />
                                    <RequiredAsterisk />
                                </Text>
                            </HelperMessage>
                            <Stack space="space.100">
                                <Text color="color.text.subtle">
                                    <FormattedMessage
                                        {...messages.requestAssistanceModalDescription}
                                    />
                                </Text>

                                <Field<ValueType<Option>>
                                    name="ticketId"
                                    label={intl.formatMessage(
                                        messages.requestAssistanceModalDropdownLabel
                                    )}
                                    aria-required={true}
                                    isRequired
                                >
                                    {({
                                        fieldProps: { id, ...rest },
                                        error
                                    }) => (
                                        <>
                                            <Select<Option>
                                                inputId={id}
                                                {...rest}
                                                options={
                                                    customerSupportRequestOptions
                                                }
                                                maxMenuHeight={200}
                                                isLoading={
                                                    isSupportRequestsLoading
                                                }
                                                filterOption={(
                                                    option,
                                                    inputValue
                                                ) => {
                                                    return searchDropdownOptions(
                                                        option.data,
                                                        inputValue
                                                    );
                                                }}
                                            />
                                            {error && (
                                                <ErrorMessage>
                                                    {error}
                                                </ErrorMessage>
                                            )}
                                        </>
                                    )}
                                </Field>
                                <StyledTextAreaWraper>
                                    <Field
                                        aria-required={true}
                                        name="comment"
                                        label={intl.formatMessage(
                                            messages.requestAssistanceModalCommentLabel
                                        )}
                                        isRequired
                                    >
                                        {({ fieldProps, error }) => (
                                            <>
                                                {/* @ts-ignore */}
                                                <TextArea
                                                    autoComplete="off"
                                                    {...fieldProps}
                                                    isRequired={false} // disables default textarea behaviour to keep UX consistent
                                                    resize="auto"
                                                    maxHeight="30vh"
                                                    minimumRows={7}
                                                />

                                                {error && (
                                                    <ErrorMessage>
                                                        {error}
                                                    </ErrorMessage>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </StyledTextAreaWraper>
                            </Stack>
                        </Stack>
                    </ModalBody>
                    <ModalFooter>
                        <ButtonGroup>
                            <Button
                                appearance="subtle"
                                onClick={handleCloseModal}
                            >
                                <FormattedMessage
                                    {...messages.requestAssistanceModalCancelButton}
                                />
                            </Button>
                            <Button
                                type="submit"
                                appearance="primary"
                                isLoading={submitting}
                            >
                                <FormattedMessage
                                    {...messages.requestAssistanceModalSendButton}
                                />
                            </Button>
                        </ButtonGroup>
                    </ModalFooter>
                </form>
            )}
        </Form>
    );
};
